import { useCallback } from 'react';
import { captureException } from '@sentry/react';
import { Query } from '@tanstack/react-query';
import { isCancel } from 'axios';

import { ToastType, useToast } from '@xemplo/toast';

import { GlobalErrorHandler } from './query-client-defaults.helper';

const QUERY_ERROR_KEY = 'QUERY_ERROR_THROWN';
const MUTATION_ERROR_KEY = 'MUTATION_ERROR_THROWN';

export function useQueryClientErrorHandlers() {
  const { addToast } = useToast();

  const handleError = useCallback(
    (err: Error, throwError?: boolean) => {
      const message = GlobalErrorHandler.getErrorMessage(err);

      captureException(err, {
        extra: {
          normalizedError: message,
        },
        tags: { flow: `global-error-handler-${throwError ? 'throw-error' : 'on-error'}` },
      });

      addToast({ text: message, type: ToastType.Alert, duration: 5000 });

      return false;
    },
    [addToast],
  );

  // On mutations, we need to use onError

  const onError = useCallback(
    (err: Error, variants: unknown) => {
      // We don't really care about cancelled requests
      // It usually means the user navigated away

      if (isCancel(err)) return false;

      // This is to prevent showing the same error
      // multiple times on re-renders. For mutations, we use the variants
      const hash = GlobalErrorHandler.hashObject(variants);
      const hasThrownBefore = GlobalErrorHandler.checkIsThrown(MUTATION_ERROR_KEY, hash);
      if (hasThrownBefore) return false;

      GlobalErrorHandler.addToSessionStorage(MUTATION_ERROR_KEY, hash);

      return handleError(err);
    },
    [handleError],
  );

  // Used for the query default

  const throwOnError = useCallback(
    (err: Error, query: Query) => {
      // We don't really care about cancelled requests
      // It usually means the user navigated away

      if (isCancel(err)) return false;

      // This is to prevent showing the same error
      // multiple times on re-renders. For queries, we use the queryKey
      // to identify the query and check if it has been thrown before
      const hash = GlobalErrorHandler.hashObject(query.queryKey);
      const hasThrownBefore = GlobalErrorHandler.checkIsThrown(QUERY_ERROR_KEY, hash);
      if (hasThrownBefore) return false;

      GlobalErrorHandler.addToSessionStorage(QUERY_ERROR_KEY, hash);

      return handleError(err, true);
    },
    [handleError],
  );

  return { throwOnError, onError };
}
